import { createTheme } from "@mui/material/styles";
import type { ActionType } from "./generated/core-types";

export const palette = {
  cream: "#FBF7F3",
  surface: "#FFFFFF",
  ink: "#241F1E",
  inkSoft: "#706A68",
  border: "#EADDD6",
  borderStrong: "#CDB8AD",

  saffron: "#FFB020",
  saffronDark: "#AD604E",

  sage: "#238752",
  sageBg: "#E9F5EC",

  vermillion: "#B55C47",
  vermillionBg: "#FBEDE8",

  inkBlue: "#5C6BA0",
  inkBlueBg: "#E9EDF9",

  plum: "#91639D",
  plumBg: "#F4EAF5",

  slate: "#77716F",
  slateBg: "#F1ECE9",
} as const;

export const actionPalette: Record<
  ActionType,
  { fg: string; bg: string; label: string }
> = {
  BUY: { fg: palette.sage, bg: palette.sageBg, label: "Mua hàng" },
  REVIEW: { fg: palette.vermillion, bg: palette.vermillionBg, label: "Cần xem lại" },
  QUESTION: { fg: palette.inkBlue, bg: palette.inkBlueBg, label: "Câu hỏi" },
  NOTHING: { fg: palette.slate, bg: palette.slateBg, label: "—" },
  CONTEST: { fg: palette.plum, bg: palette.plumBg, label: "Cuộc thi" },
};

export const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: palette.cream, paper: palette.surface },
    text: { primary: palette.ink, secondary: palette.inkSoft },
    primary: { main: palette.saffronDark, contrastText: palette.surface },
    secondary: { main: palette.sage },
    error: { main: palette.vermillion },
    divider: palette.border,
  },
  typography: {
    fontFamily: '"Geist", "Helvetica Neue", system-ui, sans-serif',
    h1: {
      fontFamily: '"Fraunces Variable", Georgia, serif',
      fontWeight: 500,
      letterSpacing: "-0.02em",
      fontSize: "2.25rem",
      lineHeight: 1.1,
    },
    h2: {
      fontFamily: '"Fraunces Variable", Georgia, serif',
      fontWeight: 500,
      fontSize: "1.5rem",
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontFamily: '"Fraunces Variable", Georgia, serif',
      fontWeight: 500,
      fontSize: "1.15rem",
      lineHeight: 1.3,
    },
    overline: {
      fontFamily: '"Geist", system-ui, sans-serif',
      letterSpacing: "0.18em",
      fontWeight: 500,
      fontSize: "0.7rem",
      textTransform: "uppercase",
      lineHeight: 1,
    },
    body1: { fontSize: "0.95rem", lineHeight: 1.55 },
    body2: { fontSize: "0.85rem", lineHeight: 1.5, color: palette.inkSoft },
    button: { fontWeight: 500, letterSpacing: "0.01em" },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.cream,
          color: palette.ink,
          fontFeatureSettings: '"ss01", "ss03", "cv11"',
        },
        "::selection": {
          background: palette.saffron,
          color: palette.ink,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          boxShadow: "none",
          borderRadius: 6,
          fontWeight: 500,
          "&:hover": { boxShadow: "none" },
        },
        containedPrimary: {
          background: "#AD604E",
          color: palette.surface,
          "&:hover": { background: "#954D3D" },
          "&.Mui-disabled": {
            backgroundColor: palette.border,
            color: palette.inkSoft,
          },
        },
        outlined: {
          borderColor: palette.border,
          color: palette.ink,
          "&:hover": {
            borderColor: palette.ink,
            backgroundColor: "transparent",
          },
        },
        text: {
          color: palette.ink,
          "&:hover": { backgroundColor: palette.sageBg },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined", size: "small" },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: palette.surface,
            fontFamily: '"Geist", system-ui, sans-serif',
            "& fieldset": { borderColor: palette.border },
            "&:hover fieldset": { borderColor: palette.inkSoft },
            "&.Mui-focused fieldset": {
              borderColor: palette.ink,
              borderWidth: 1,
            },
          },
          "& .MuiInputLabel-root": {
            fontFamily: '"Geist", system-ui, sans-serif',
            color: palette.inkSoft,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: palette.cream,
          borderLeft: `1px solid ${palette.border}`,
        },
      },
    },
  },
});

declare module "@mui/material/styles" {
  interface Palette {
    atelier: typeof palette;
  }
  interface PaletteOptions {
    atelier?: typeof palette;
  }
}
